import { Link } from 'react-router-dom';
import type { Project } from '@faktorplus/shared';
import { useArchivedProjects, useUnarchiveProject } from '../../api/projects';
import { Button } from '../../components/ui/button';
import { EmptyState, ErrorBox, LoadingBlock, Panel } from '../../components/ui/feedback';
import { ApiError } from '../../lib/api-client';
import { formatShortDate, plural } from '../../lib/format';
import pageStyles from '../../components/layout/page.module.css';
import styles from './projects.module.css';

/**
 * The site archive (24.09.2026): settled constructions kept whole — invoices,
 * documents, photos — for the warranty years, grouped by the year they were
 * closed, "archiwum budowy w danym roku". Office only; a card opens read-on,
 * and a mistaken archive is one click reversible.
 */
export function ArchivedProjectsPage() {
  const { data: projects = [], isLoading, error } = useArchivedProjects();
  const unarchive = useUnarchiveProject();

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBox>Nie udało się pobrać archiwum budów.</ErrorBox>;

  const byYear = groupByYear(projects);

  return (
    <>
      <header className={pageStyles.header}>
        <Link to="/budowy" className={styles.backLink}>
          ← Budowy
        </Link>
        <h1 className={pageStyles.title}>Archiwum budów</h1>
        <span className={pageStyles.subtitle}>
          {projects.length}{' '}
          {plural(projects.length, [
            'zakończona budowa',
            'zakończone budowy',
            'zakończonych budów',
          ])}
        </span>
      </header>

      {unarchive.error && (
        <ErrorBox>
          {unarchive.error instanceof ApiError
            ? unarchive.error.message
            : 'Nie udało się przywrócić budowy.'}
        </ErrorBox>
      )}

      {projects.length === 0 && (
        <EmptyState
          mark="∅"
          title="Archiwum jest puste"
          description="Zakończone budowy archiwizuje się z ich karty — przyciskiem „Zakończ i zarchiwizuj”."
        />
      )}

      {byYear.map(([year, yearProjects]) => (
        <Panel key={year} title={String(year)}>
          <div className={pageStyles.tableWrap}>
            <table className={pageStyles.table}>
              <thead>
                <tr>
                  <th>Kod</th>
                  <th>Nazwa</th>
                  <th className={pageStyles.hideOnPhone}>Zarchiwizowana</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {yearProjects.map((project) => (
                  <tr key={project.id}>
                    <td className={pageStyles.docNumber}>
                      <Link to={`/budowy/${project.id}`} className={styles.rowLink}>
                        {project.code}
                      </Link>
                    </td>
                    <td className={pageStyles.strong}>
                      <Link to={`/budowy/${project.id}`} className={styles.rowLink}>
                        {project.name}
                      </Link>
                    </td>
                    <td className={`${pageStyles.muted} ${pageStyles.hideOnPhone}`}>
                      {formatShortDate(project.archivedAt)}
                    </td>
                    <td>
                      <div className={pageStyles.rowActions}>
                        <Button
                          isLoading={
                            unarchive.isPending && unarchive.variables === project.id
                          }
                          onClick={() => unarchive.mutate(project.id)}
                        >
                          Przywróć
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      ))}
    </>
  );
}

/** Newest year first; the API already sorts within a year by archive date. */
function groupByYear(projects: Project[]): Array<[number, Project[]]> {
  const groups = new Map<number, Project[]>();

  for (const project of projects) {
    const year = new Date(project.archivedAt!).getFullYear();
    const group = groups.get(year) ?? [];
    group.push(project);
    groups.set(year, group);
  }

  return [...groups.entries()].sort(([a], [b]) => b - a);
}
